import { useEffect, useState, ChangeEvent, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AssetModel, TransactionModel } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  useAssetGetAll,
  useAssetGetOne,
  useTransactionGetAll,
  useTransactionGetOne,
  useTransactionDelete,
} from "@providers/index";
import { useNotify, useDebounce, useClickOutside } from "@hooks/index";
import {
  SearchSvg,
  SearchAssetItem,
  SearchTransactionItem,
} from "@components/index";
import { AssetDetails, AssetForm, TransactionDetails } from "@pages/index";

export const SearchBar = () => {
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const queryClient = useQueryClient();

  const [showSearch, setShowSearch] = useState(false);
  const searchBarRef = useRef(null);
  useClickOutside(searchBarRef, () => setShowSearch(false));

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const [assets, setAssets] = useState<AssetModel[]>([]);
  const [assetId, setAssetId] = useState("");
  const [assetDetails, setAssetDetails] = useState<AssetModel>();
  const [isUpdate, setIsUpdate] = useState(false);

  const [transactions, setTransactions] = useState<TransactionModel[]>([]);
  const [transactionId, setTransactionId] = useState("");
  const [transactionDetails, setTransactionDetails] =
    useState<TransactionModel>();

  const {
    isPending: isPendingGetAssets,
    isSuccess: isSuccessGetAssets,
    isStale: isStaleGetAssets,
    data: dataGetAssets,
    isError: isErrorGetAssets,
    error: errorGetAssets,
  } = useAssetGetAll(
    !disabledFeatures.includes("Assets") ? debouncedSearchValue : "",
  );

  useEffect(() => {
    if (isSuccessGetAssets && dataGetAssets) {
      handleCloseAssetDetails();
      setAssets(dataGetAssets);
    }
  }, [isSuccessGetAssets, dataGetAssets]);

  useEffect(() => {
    if (isErrorGetAssets && errorGetAssets) {
      notify("error", errorGetAssets.message);
    }
  }, [isErrorGetAssets, errorGetAssets, notify]);

  const {
    isPending: isPendingGetAsset,
    isSuccess: isSuccessGetAsset,
    isStale: isStaleGetAsset,
    data: dataGetAsset,
    isError: isErrorGetAsset,
    error: errorGetAsset,
  } = useAssetGetOne(assetId);

  useEffect(() => {
    if (isErrorGetAsset && errorGetAsset) {
      notify("error", errorGetAsset.message);
    }
  }, [isErrorGetAsset, errorGetAsset, notify]);

  useEffect(() => {
    if (isSuccessGetAsset && dataGetAsset) {
      setAssetDetails(dataGetAsset);
    }
  }, [isSuccessGetAsset, dataGetAsset]);

  const {
    isPending: isPendingGetTransactions,
    isSuccess: isSuccessGetTransactions,
    isStale: isStaleGetTransactions,
    data: dataGetTransactions,
    isError: isErrorGetTransactions,
    error: errorGetTransactions,
  } = useTransactionGetAll({
    isSearchUsing: true,
    search: debouncedSearchValue,
  });

  useEffect(() => {
    if (isSuccessGetTransactions && dataGetTransactions) {
      handleCloseTransactionDetails();
      setTransactions(dataGetTransactions.transactions);
    }
  }, [isSuccessGetTransactions, dataGetTransactions]);

  useEffect(() => {
    if (isErrorGetTransactions && errorGetTransactions) {
      notify("error", errorGetTransactions.message);
    }
  }, [isErrorGetTransactions, errorGetTransactions, notify]);

  const {
    isPending: isPendingGetTransaction,
    isSuccess: isSuccessGetTransaction,
    isStale: isStaleGetTransaction,
    data: dataGetTransaction,
    isError: isErrorGetTransaction,
    error: errorGetTransaction,
  } = useTransactionGetOne(transactionId);

  useEffect(() => {
    if (isErrorGetTransaction && errorGetTransaction) {
      notify("error", errorGetTransaction.message);
    }
  }, [isErrorGetTransaction, errorGetTransaction, notify]);

  useEffect(() => {
    if (isSuccessGetTransaction && dataGetTransaction) {
      setTransactionDetails(dataGetTransaction);
    }
  }, [isSuccessGetTransaction, dataGetTransaction]);

  const {
    mutate: mutateDeleteTransaction,
    isPending: isPendingDeleteTransaction,
  } = useTransactionDelete(
    () => {
      notify("success", "Your Transaction has been successfully deleted.");
      handleCloseTransactionDetails();
      queryClient.invalidateQueries({ queryKey: ["transactionGetAll"] });
    },
    ({ message }) => notify("error", message),
  );

  useEffect(() => {
    setState({
      isLoading:
        (isStaleGetAssets && isPendingGetAssets) ||
        (isStaleGetAsset && isPendingGetAsset) ||
        (isStaleGetTransactions && isPendingGetTransactions) ||
        (isStaleGetTransaction && isPendingGetTransaction) ||
        isPendingDeleteTransaction,
    });
  }, [
    isStaleGetAssets,
    isPendingGetAssets,
    isStaleGetAsset,
    isPendingGetAsset,
    isStaleGetTransactions,
    isPendingGetTransactions,
    isStaleGetTransaction,
    isPendingGetTransaction,
    isPendingDeleteTransaction,
    setState,
  ]);

  const handleSearchChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(target.value);
    setTransactions([]);
  };

  const handleCloseAssetDetails = () => {
    setAssetId("");
    setAssetDetails(undefined);
    setIsUpdate(false);
  };

  const handleCloseTransactionDetails = () => {
    setTransactionId("");
    setTransactionDetails(undefined);
  };

  return (
    <div
      ref={searchBarRef}
      className="relative mx-4 mt-2 flex h-[48px] items-center rounded-lg bg-white-16 md:mx-0 md:mt-0 md:w-[300px] lg:w-[480px]"
    >
      <input
        name="searchBar"
        value={searchValue}
        onFocus={() => setShowSearch(true)}
        onChange={handleSearchChange}
        className="w-full border-0 bg-transparent pl-4 pr-12 text-white-100 outline-none placeholder:text-white-54"
        placeholder={`Search by: Description, Amount${!disabledFeatures.includes("Assets") ? ", Asset" : ""}`}
      />
      <div className="absolute right-4 text-white-100">
        <SearchSvg />
      </div>
      {assetDetails && !isUpdate && (
        <AssetDetails
          asset={assetDetails}
          onEdit={() => setIsUpdate(true)}
          onClose={handleCloseAssetDetails}
        />
      )}
      {assetDetails && isUpdate && (
        <AssetForm
          isUpdating
          asset={assetDetails}
          onClose={handleCloseAssetDetails}
        />
      )}
      {transactionDetails && (
        <TransactionDetails
          transaction={transactionDetails}
          onRemove={() => mutateDeleteTransaction(transactionId)}
          onClose={handleCloseTransactionDetails}
        />
      )}
      {showSearch && (
        <div className="absolute top-14 z-10 flex max-h-96 w-full flex-col gap-y-2 overflow-y-auto rounded-lg bg-white-100 py-4 shadow-sm">
          {!!assets?.length &&
            assets.map((asset) => (
              <SearchAssetItem
                key={asset.uniqueId}
                asset={asset}
                onDetails={() => setAssetId(asset.uniqueId)}
              />
            ))}
          {!!transactions?.length &&
            transactions.map((transaction) => (
              <SearchTransactionItem
                key={transaction.uniqueId}
                transaction={transaction}
                onDetails={() => setTransactionId(transaction.uniqueId)}
              />
            ))}
          {!assets?.length && !transactions?.length && (
            <div className="text-center text-300">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};
